import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import SectionShell from "@/components/landing/SectionShell";
import NewsletterSignup from "@/components/landing/NewsletterSignup";
import PageFooter from "@/components/landing/PageFooter";
import RelatedPages from "@/components/landing/RelatedPages";
import { FORRAS_KOD_PAGES } from "@/data/navigation";
import TestimonialsHighlight from "@/components/landing/TestimonialsHighlight";

/**
 * Forrás-Kód® Kísérés — új önálló oldal.
 * Használt vizuális rendszer: SectionShell, Cormorant Garamond + Inter,
 * bg-warm / bg-tinted ritmus, hairline elválasztók, finom arany kiemelések.
 * Nincs új arculati elem, nincs AI/stock kép — a képhelyek később töltendők.
 */
export default function ForrasKodKiseres() {
  // Page-specific SEO (per brief) without touching global index.html defaults.
  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");

    document.title = "Forrás-Kód® Kísérés | Szász-Tóth Enikő";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      "content",
      "Egyéni és csoportos Forrás-Kód® Kísérés Szász-Tóth Enikővel. Mélyebb rálátás az élethelyzetedre, a működéseidre és a változás lehetőségeire."
    );

    return () => {
      document.title = previousTitle;
      if (previousDescription && metaDescription) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <main
      data-testid="forras-kod-kiseres-page"
      className="bg-warm text-graphite min-h-screen"
    >
      <Header />
      <div className="pt-24 md:pt-28" />

      {/* ------------------------------------------------------------------- */}
      {/* HERO                                                                 */}
      {/* ------------------------------------------------------------------- */}
      <section
        data-testid="fkk-hero"
        className="relative px-6 md:px-10 py-10 md:py-14"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-[11px] md:text-[12px] tracking-[0.42em] uppercase text-gold mb-6">
            — Hivatalos Forrás-Kód® Kísérő
          </p>
          <h1
            data-testid="fkk-hero-title"
            className="font-serif-display text-[40px] sm:text-[52px] md:text-[62px] lg:text-[68px] leading-[1.08] text-graphite tracking-tight mb-10"
          >
            Forrás-Kód
            <sup className="text-gold text-[0.4em] align-super ml-0.5 relative -top-2 md:-top-3">
              ®
            </sup>{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              Kísérés
            </em>
          </h1>
          <p className="font-serif-display italic text-graphite text-[20px] sm:text-[23px] md:text-[26px] leading-[1.55] max-w-2xl mx-auto">
            Rálátás arra, ami valóban működteti az életed folyamatait.
          </p>
          <p className="mt-5 font-serif-display italic text-graphite-soft text-[18px] sm:text-[20px] md:text-[22px] leading-[1.6] max-w-2xl mx-auto">
            És lehetőség arra, hogy tudatosan építsd azt,
            <br className="hidden md:block" /> amit szeretnél helyette.
          </p>
        </div>

        {/* Hero image — group women conversation (Forrás-Kód® Kísérés) */}
        <figure
          data-testid="fkk-hero-image"
          className="mt-14 md:mt-16 max-w-4xl mx-auto"
        >
          <div className="portrait-frame relative">
            <img
              src="/images/forras-kod-kiseres-hero.jpg"
              alt="Forrás-Kód® Kísérés — közös beszélgetés, őszinte kapcsolódás nők között"
              className="w-full h-auto block"
              style={{ filter: "saturate(0.98) contrast(1.02)" }}
              loading="eager"
            />
          </div>
        </figure>
      </section>

      {/* ------------------------------------------------------------------- */}
      {/* NYITÓ GONDOLAT                                                      */}
      {/* ------------------------------------------------------------------- */}
      <section
        data-testid="fkk-opening-question"
        className="px-6 md:px-10 pt-2 md:pt-4 pb-10 md:pb-14"
      >
        <div
          className="max-w-2xl mx-auto border border-gold bg-tinted px-8 md:px-12 py-10 md:py-14 text-center"
          style={{ boxShadow: "0 10px 40px -24px rgba(160, 122, 40, 0.22)" }}
        >
          <p className="font-serif-display italic text-graphite text-[20px] md:text-[24px] leading-[1.55]">
            Mi lenne, ha az életedben visszatérő helyzetek nem csupán problémák lennének,
            hanem lehetőségek arra, hogy egészen új nézőpontból{" "}
            <span className="text-gold not-italic">ismerd meg önmagad</span>?
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------------- */}
      {/* BEVEZETŐ SZÖVEG                                                      */}
      {/* ------------------------------------------------------------------- */}
      <SectionShell testId="fkk-intro" tight>
        <p>
          Sokszor pontosan tudjuk, min szeretnénk változtatni. Látjuk, hogy bizonyos
          helyzetek, kapcsolati dinamikák vagy döntések újra és újra hasonló eredményhez
          vezetnek. Megpróbálunk másképp működni, változtatni, új irányt választani —
          mégis vannak területek, ahol valami újra visszavisz ugyanoda.
        </p>
        <p className="text-center">
          Ilyenkor nem feltétlenül az a kérdés, hogy mit kellene másképp csinálnod.
        </p>
        <p className="font-serif-display italic text-graphite text-center text-[22px] md:text-[26px] leading-[1.5] my-10 md:my-12">
          Hanem az, hogy mi az, amit még nem látsz abból,
          <br className="hidden md:block" />
          ami a jelenlegi működésedet{" "}
          <span className="text-gold">fenntartja</span>.
        </p>
        <p>
          A Forrás-Kód<sup className="text-gold">®</sup> Kísérés abban támogat, hogy
          mélyebb rálátást kapj a saját élethelyzetedre, felismerd azokat a működéseket
          és összefüggéseket, amelyek valóban hatással vannak arra, ahol most tartasz,
          majd ebből a rálátásból elindulhass egy számodra működő új irányba.
        </p>

        {/* Highlighted thought box — unified typography & size, centered */}
        <div
          data-testid="fkk-intro-highlight"
          className="my-10 md:my-14 bg-tinted border border-gold px-7 md:px-10 py-10 md:py-12 text-center max-w-xl mx-auto"
          style={{ boxShadow: "0 10px 40px -24px rgba(160, 122, 40, 0.22)" }}
        >
          <p className="font-serif-display text-graphite text-[20px] sm:text-[22px] md:text-[24px] leading-[1.55]">
            Nemcsak azt nézzük meg, mit szeretnél
            <br />
            magad mögött hagyni.
          </p>
          <p className="mt-5 md:mt-6 font-serif-display text-graphite text-[20px] sm:text-[22px] md:text-[24px] leading-[1.55]">
            Legalább ilyen fontos kérdés:
          </p>
          <p className="mt-5 md:mt-6 font-serif-display text-graphite text-[20px] sm:text-[22px] md:text-[24px] leading-[1.55]">
            Mit szeretnél felépíteni{" "}
            <span className="text-gold">helyette?</span>
          </p>
        </div>
      </SectionShell>

      {/* ------------------------------------------------------------------- */}
      {/* MI A FORRÁS-KÓD?                                                    */}
      {/* ------------------------------------------------------------------- */}
      <SectionShell
        testId="fkk-what-is"
        eyebrow="Mi a Forrás-Kód®?"
        titleStart="Szemlélet és"
        titleAccent="élő energiarendszer"
        tinted
      >
        <p>
          A Forrás-Kód<sup className="text-gold">®</sup> egy olyan szemlélet és élő
          energiarendszer, amely segít más nézőpontból rálátni az életedben megjelenő
          helyzetekre, visszatérő működésekre és elakadásokra.
        </p>
        <p>
          A közös munka során feltérképezzük a jelenlegi élethelyzetedet, megnézzük a kapcsolódó
          mintákat és működéseket, valamint azt, hogy mi lehet az, ami valójában akadályozza a
          változást vagy a továbblépést. A rálátás lehetőséget ad arra, hogy egyre tisztábban
          lásd a saját működésedet, felismerd a rendelkezésedre álló lehetőségeket, és ezek
          alapján olyan döntéseket hozz, amelyek már abba az irányba visznek, amerre valóban
          szeretnél haladni.
        </p>
      </SectionShell>

      {/* ------------------------------------------------------------------- */}
      {/* HOGYAN SEGÍT A FOLYAMAT?                                            */}
      {/* ------------------------------------------------------------------- */}
      <section
        id="hogyan-segit"
        data-testid="fkk-how-it-helps"
        className="relative px-6 md:px-10 py-10 md:py-14"
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body text-center">
            — Hogyan segít a folyamat?
          </p>
          <h2 className="font-serif-display text-[34px] sm:text-[40px] md:text-[46px] leading-[1.18] text-graphite mb-10 text-center">
            Ahhoz, hogy lásd,{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              merre tovább
            </em>
            ,
          </h2>

          <div className="text-center text-[17px] md:text-[18px] leading-[1.9] text-graphite font-body font-light space-y-6">
            <p className="font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.5] mb-10 md:mb-12">
              először meg kell értened,{" "}
              <span className="text-gold">hol vagy most</span>.
            </p>
            <div className="reading-prose text-[16px] md:text-[17px]" data-testid="fkk-how-it-helps-body">
              <p>
                Közösen feltérképezzük a jelenlegi élethelyzetedet: milyen kihívásokkal
                küzdesz, hol érzel elakadást, és miben szeretnél változást. Áttekintjük,
                milyen visszatérő életminták, belső működések, döntések és körülmények
                vezethettek ide, illetve mi nehezítheti a továbblépést. A Forrás-Kód saját
                szemlélete alapján értelmezem a megosztott élethelyzetet, és konkrét
                változtatási javaslatokat fogalmazok meg. Ezeket közösen átbeszéljük, hogy
                mérlegelni tudd, hogyan alkalmazhatók a saját életedben.
              </p>
            </div>
            <p className="font-serif-display italic text-graphite-soft text-[17px] md:text-[19px] leading-[1.65] pt-6 max-w-xl mx-auto">
              A szolgáltatás nem garantál meghatározott eredményt. A folyamat célja,
              hogy tisztábban lásd a helyzetedet, és átgondolt, követhető lépéseket tudj
              tenni.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-10 md:mt-12 hairline" />
      </section>

      {/* ------------------------------------------------------------------- */}
      {/* HOGYAN ZAJLIK A KÖZÖS FOLYAMAT?  (3 lépcső)                          */}
      {/* ------------------------------------------------------------------- */}
      <ProcessSteps />

      {/* ------------------------------------------------------------------- */}
      {/* SZOLGÁLTATÁSOK ÁTTEKINTÉSE  (3 kártya)                               */}
      {/* ------------------------------------------------------------------- */}
      <ServicesOverview />

      {/* ------------------------------------------------------------------- */}
      {/* KÉT SZOLGÁLTATÁSBLOKK — EGYÉNI + CSOPORTOS                           */}
      {/* ------------------------------------------------------------------- */}
      <ServicesBlock />

      {/* ------------------------------------------------------------------- */}
      {/* MIÉRT KAPOTT HELYET NÁLAM A FORRÁS-KÓD®?                             */}
      {/* ------------------------------------------------------------------- */}
      <section
        id="miert-kapott-helyet"
        data-testid="fkk-why-here"
        className="relative px-6 md:px-10 py-14 md:py-20 bg-tinted border-t border-hairline"
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body text-center">
            — Mit jelent számomra
          </p>
          <h2 className="font-serif-display text-[32px] sm:text-[38px] md:text-[44px] leading-[1.2] text-graphite mb-12 md:mb-14 text-center">
            Mit jelent számomra
            <br />
            a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              Forrás-Kód<sup className="text-gold">®</sup>?
            </em>
          </h2>

          {/* Personal portrait — Szász-Tóth Enikő */}
          <figure
            data-testid="fkk-personal-image"
            className="mb-14 md:mb-16 max-w-md mx-auto"
          >
            <div className="portrait-frame relative">
              <img
                src="/images/forras-kod-kiseres-portrait.jpg"
                alt="Szász-Tóth Enikő — hivatalos Forrás-Kód® Kísérő"
                className="w-full h-auto block"
                style={{ filter: "saturate(0.98) contrast(1.02)" }}
                loading="lazy"
              />
            </div>
          </figure>

          <div className="reading-prose text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
            <p>
              A Forrás-Kód nem egy új módszer, vagy technika. Sokkal inkább egy élő energia rendszer
              és teljes tudatváltás lehetősége. Az elmúlt három évben én magam is folyamatosan ebben
              az energia rendszerben fejlődtem. Kezdetben elsősorban a saját egyéni folyamatainkon
              dolgoztunk, és a saját fejlődésünk volt a fókusz. Ekkor még 3 hónapos kismamaként
              kezdtem ezt a transzformációs folyamatot és azóta szó szerint minden más
              megvilágításba került, illetve mindaz értelmet nyert, ami belülről mindig is hívott.
              Majd ahogy egyre több minden került a helyére bennem Önmagammal és az emberi
              működéssel kapcsolatban, lassan elkezdett kibontakozni az is, hogy mindabból, amit én
              magam megtapasztaltam és felismertem, mit szeretnék tovább vinni, hogyan tud az egyéni
              misszióm kibontakozni.
            </p>
            <p>
              Idővel egyre tisztábban kezdtem látni a saját irányomat, a munkámat és azt a missziót,
              amely ma már jóval túlmutat azon, hogy egy-egy nehéz élethelyzetre megoldást
              keressünk. Egyre inkább az kezdett érdekelni, hogyan működünk valójában az életünkben
              és a kapcsolatainkban, mit viszünk tovább és mit építünk abból, ahogyan ma választunk,
              döntünk és kapcsolódunk.
            </p>
            <aside
              data-testid="fkk-change-box"
              className="!my-9 md:!my-11 mx-auto max-w-2xl bg-warm border border-hairline px-7 md:px-12 py-8 md:py-10"
              style={{ boxShadow: "0 10px 40px -32px rgba(160, 122, 40, 0.3)" }}
            >
              <p className="font-serif-display italic text-graphite text-center text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45]" style={{ textAlign: "center", hyphens: "none", WebkitHyphens: "none" }}>
                Számomra a változás nem ér véget az egyénnél. Hatással van arra, hogyan
                kapcsolódunk a párunkhoz, a gyerekeinkhez, a családunkhoz és a
                környezetünkhöz. Arra, milyen mintákat és működéseket viszünk tovább, és
                végső soron arra is, milyen{" "}
                <span className="text-gold">kapcsolódási kultúrát</span> építünk magunk
                körül.
              </p>
            </aside>
            <p>
              A Forrás-Kód energiarendszere az elmúlt három évben ennek az útnak fontos
              része és a fejlődési folyamat biztonságos tere volt számomra. Most pedig, hivatalos Forrás-Kód<sup className="text-gold">®</sup>{" "}
              Kísérőként elérkezett az a pont, amikor ezt a szemléletet és folyamatot
              már nemcsak a saját fejlődésem részeként szeretném tovább vinni, hanem
              azok számára is elérhetővé tenni, akik velem szeretnének dolgozni.
            </p>
            <p
              data-testid="fkk-central-place"
              className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] mt-10 md:mt-12 max-w-3xl mx-auto"
              style={{ textAlign: "center", hyphens: "none", WebkitHyphens: "none" }}
            >
              Ezért kap mostantól{" "}
              <span className="text-gold">központi helyet a munkámban</span>
              <br className="hidden sm:block" /> az egyéni és csoportos{" "}
              <span className="whitespace-nowrap">
                Forrás&#8209;Kód
                <sup className="text-gold text-[0.5em] not-italic">®</sup> Kísérés.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------- */}
      {/* ZÁRÓ: SZÁSZ-TÓTH ENIKŐ + KAPCSOLÓDÁS                                 */}
      {/* ------------------------------------------------------------------- */}
      <ClosingBlock />

      {/* ------------------------------------------------------------------- */}
      {/* VISSZAJELZÉSEK KIEMELÉSE                                             */}
      {/* ------------------------------------------------------------------- */}
      <TestimonialsHighlight testIdPrefix="fkk" />

      {/* ------------------------------------------------------------------- */}
      {/* ZÁRÓ JELENTKEZÉSI SZEKCIÓ                                            */}
      {/* ------------------------------------------------------------------- */}
      <FinalInvitation />
      <RelatedPages
        pages={FORRAS_KOD_PAGES.filter((p) => p.to !== "/forras-kod-kiseres")}
        testId="related-pages-forras-kod"
      />

      <NewsletterSignup source="forras-kod-kiseres" />

      <PageFooter />
    </main>
  );
}

/* ===================================================================== */
/* Services block — Egyéni + Csoportos kísérés                            */
/* ===================================================================== */
function ServicesBlock() {
  return (
    <section
      id="szolgaltatasok"
      data-testid="fkk-services"
      className="relative px-6 md:px-10 py-14 md:py-20 border-t border-hairline"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body">
            — Közös munka
          </p>
          <h2 className="font-serif-display text-[32px] sm:text-[38px] md:text-[44px] leading-[1.2] text-graphite max-w-2xl mx-auto">
            Egyéni és csoportos{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              kísérés
            </em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {/* --- EGYÉNI KÍSÉRÉS ---------------------------------------- */}
          <ServiceCard
            testId="fkk-service-individual"
            anchorId="egyeni-kiseres"
            tag="Egyéni kísérés"
            title="Személyes figyelem"
            titleAccent="a te helyzetedre"
            bodyParagraphs={[
              "Az egyéni alkalmon elmondod, mi foglalkoztat, és miben kérsz segítséget. Nem kell előre összeraknod a teljes történetet: a kérdéseim segítenek tisztázni a lényeges részleteket.",
              "Átbeszéljük a javasolt változtatásokat: mit jelentenek a gyakorlatban, milyen lehetőségeid vannak a megvalósításukra, és mi lehet az első lépés. Kérdezhetsz, és tisztázhatjuk azt is, ami bizonytalan számodra.",
            ]}
            chooseIntro="Az egyéni kísérést válaszd, ha"
            chooseText="egy konkrét élethelyzetre szeretnél összpontosítani, vagy olyan kérdésről beszélnél, amelyet négyszemközt osztanál meg."
            duration="45 perc"
            price="34 000 Ft"
            priceSuffix="/ alkalom"
            priceNote="A második alkalomtól 30 000 Ft / alkalom kedvezményes díj érvényes, ha további 6 alkalmat vásárolsz."
            ctaLabel="Jelentkezem egyéni Forrás-Kód® Kísérésre"
            ctaTestId="fkk-cta-individual"
            ctaHref="#kapcsolodas"
          />

          {/* --- CSOPORTOS KÍSÉRÉS ------------------------------------- */}
          <ServiceCard
            testId="fkk-service-group"
            anchorId="csoportos-kiseres"
            tag="Csoportos kísérés"
            title="Közös téma,"
            titleAccent="több élethelyzet"
            bodyParagraphs={[
              "A csoport egy adott témával dolgozik. A résztvevők elmondják, hogyan élik meg azt, és miként jelenik meg a saját életükben. Így ugyanaz a jelenség több különböző történeten keresztül válik láthatóvá.",
              "A folyamatot én vezetem, a Forrás-Kód szemlélete alapján értelmezem a megosztott tapasztalatokat, rámutatok a lehetséges összefüggésekre, és változtatási javaslatokat fogalmazok meg.",
              "Mások történeteit hallva a saját helyzetedre is ráismerhetsz. A közös feldolgozás és a javaslataim további szempontokat adhatnak ahhoz, hogy átgondold a saját működésedet és lehetőségeidet.",
            ]}
            chooseIntro="A csoportos kísérést válaszd, ha"
            chooseText="az adott témában magadra ismersz, és szívesen megosztod a tapasztalataidat mások előtt."
            duration="120 perc"
            price="12 000 Ft"
            priceSuffix="/ fő / alkalom"
            ctaLabel="Aktuális csoportos Forrás-Kód® folyamatok"
            ctaTestId="fkk-cta-group"
            ctaHref="#kapcsolodas"
          />
        </div>

        {/* Shared closing note */}
        <div className="mt-14 md:mt-16 max-w-2xl mx-auto text-center">
          <p className="font-serif-display italic text-graphite text-[18px] md:text-[21px] leading-[1.6]">
            Mindkét formában tőlem kapsz segítséget.
            <br className="hidden md:block" />
            A különbség a személyes figyelem és a közös feldolgozás módjában van.
          </p>
        </div>

        {/* Legal note */}
        <p
          data-testid="fkk-legal-note"
          className="mt-10 md:mt-12 text-center text-[12.5px] md:text-[13px] text-muted-warm font-body font-light italic leading-[1.7] max-w-xl mx-auto"
        >
          Minden feltüntetett ár bruttó. A részletes igénybevételi, fizetési és
          lemondási feltételeket az ÁSZF tartalmazza.
        </p>
      </div>
    </section>
  );
}

/* --- Single service card (two-up on desktop) ----------------------
 * Equal-height cards: using flex-col with items-stretch on the grid,
 * and flex-grow on the body so the duration/price block aligns across
 * both cards at the same horizontal line.
 * ---------------------------------------------------------------- */
function ServiceCard({
  testId,
  anchorId,
  tag,
  title,
  titleAccent,
  bodyParagraphs,
  chooseIntro,
  chooseText,
  duration,
  price,
  priceSuffix,
  priceNote,
  ctaLabel,
  ctaTestId,
  ctaHref,
}) {
  return (
    <article
      data-testid={testId}
      id={anchorId}
      className="bg-warm border border-gold px-7 md:px-10 py-10 md:py-14 flex flex-col h-full scroll-mt-24 md:scroll-mt-28"
      style={{ boxShadow: "0 10px 40px -24px rgba(160, 122, 40, 0.22)" }}
    >
      {/* Centered header — tag + title */}
      <p className="text-[10.5px] tracking-[0.35em] uppercase text-gold font-body mb-5 text-center">
        — {tag}
      </p>
      <h3 className="font-serif-display text-[26px] sm:text-[30px] md:text-[34px] leading-[1.25] text-graphite mb-8 md:mb-10 text-center">
        {title}
        <br />
        <em className="text-gold" style={{ fontStyle: "italic" }}>
          {titleAccent}
        </em>
      </h3>

      {/* Body — justified reading prose for clean body copy */}
      <div className="reading-prose text-[16px] md:text-[16.5px] leading-[1.85] text-graphite font-body font-light space-y-4 mb-8">
        {bodyParagraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Spacer — grows so chooseIntro, price block and CTA align across cards */}
      <div className="flex-grow min-h-[16px]" aria-hidden="true" />

      {/* Choose intro — centered italic highlight (horizontally aligned across cards) */}
      <p className="font-serif-display italic text-graphite text-[17px] md:text-[18.5px] leading-[1.65] text-center">
        {chooseIntro}{" "}
        <span className="not-italic font-light text-graphite">{chooseText}</span>
      </p>

      {/* Duration + price — inline, no sub-boxes, horizontally aligned across cards */}
      <div className="mt-10 pt-8 border-t border-hairline">
        <div className="grid grid-cols-2 gap-4 md:gap-6 items-start">
          <div className="text-center">
            <p className="font-body text-[10px] md:text-[10.5px] tracking-[0.3em] uppercase text-muted-warm mb-3">
              Egy alkalom
            </p>
            <p className="font-serif-display text-graphite text-[22px] md:text-[26px] leading-none tracking-tight">
              {duration}
            </p>
          </div>
          <div className="text-center border-l border-hairline">
            <p className="font-body text-[10px] md:text-[10.5px] tracking-[0.3em] uppercase text-gold mb-3">
              Díj
            </p>
            <p className="font-serif-display text-gold text-[22px] md:text-[26px] leading-none tracking-tight">
              {price}
            </p>
            <p className="mt-2 font-body text-[11px] md:text-[12px] text-muted-warm italic font-light leading-[1.4]">
              {priceSuffix}
            </p>
          </div>
        </div>

        {/* Price note slot — hairline divider + reserved height so both cards align */}
        <div className="mt-6 pt-6 border-t border-hairline min-h-[72px]">
          {priceNote && (
            <p className="text-[12.5px] md:text-[13px] text-muted-warm font-body font-light italic leading-[1.7] text-center">
              {priceNote}
            </p>
          )}
        </div>
      </div>

      {/* CTA — anchor to in-page kapcsolódás section (no fictional URLs) */}
      <div className="mt-10 text-center">
        <a
          href={ctaHref}
          data-testid={ctaTestId}
          className="cta-gold-outline inline-flex items-center gap-3 px-7 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.14em] leading-[1.4] text-center"
        >
          {ctaLabel}
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </article>
  );
}

/* ===================================================================== */
/* Closing block — Szász-Tóth Enikő + kapcsolódás                        */
/* ===================================================================== */
function ClosingBlock() {
  return (
    <section
      id="kapcsolodas"
      data-testid="fkk-closing"
      className="relative px-6 md:px-10 py-16 md:py-24 border-t border-hairline"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center">
          <p className="text-[12px] tracking-[0.42em] uppercase text-gold mb-5 font-body">
            — Szász-Tóth Enikő
          </p>
          <p className="font-serif-display italic text-graphite-soft text-[15px] md:text-[16px] tracking-[0.08em] mb-10 md:mb-12">
            Hivatalos Forrás-Kód<sup className="text-gold">®</sup> Kísérő
          </p>
        </div>

        <div className="reading-prose text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
          <p>
            A közös munkában számomra nem az a cél, hogy kész válaszokat adjak arra, hogyan
            kellene élned az életedet. Ugyanakkor lesznek olyan pontok, amelyekre a konzultáció
            során rámutatok, hiszen ezek vezetnek majd a kiigazításhoz. Azért kísérlek, hogy egyre
            nagyobb rálátásod legyen arra, mi történik benned és körülötted, milyen lehetőségeid
            vannak, és mit szeretnél ezekből tudatosan tovább építeni.
          </p>
          <p className="font-serif-display italic text-graphite text-center text-[20px] md:text-[24px] leading-[1.5] my-10 md:my-12">
            Mert amikor már látod, mi működtetett eddig,
            <br className="hidden md:block" />
            megnyílik annak a lehetősége is, hogy{" "}
            <span className="text-gold">másképp válassz</span>.
          </p>
          <p className="text-center">
            Ha úgy érzed, elérkezett az idő arra, hogy mélyebben ráláss arra, ahol most
            tartasz, várlak szeretettel.
          </p>
        </div>

        {/* Soft invitation / CTA pair ----------------------------- */}
        <div className="mt-14 md:mt-16 pt-10 md:pt-12 border-t border-hairline text-center">
          <p className="text-[11px] md:text-[12px] tracking-[0.35em] uppercase text-muted-warm font-body mb-6">
            — Jelentkezés
          </p>
          <p className="font-body font-light text-[15px] md:text-[16px] leading-[1.85] text-graphite max-w-lg mx-auto mb-10">
            A jelentkezés részletei és az aktuális csoportos folyamatok hamarosan itt
            lesznek elérhetők. Addig is, ha úgy érzed, hogy ez az út a tiéd, keress
            bátran — személyesen beszéljük át a lehetőségeket.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 sm:gap-5">
            <Link
              to="/konzultacio#kapcsolat"
              data-testid="fkk-closing-cta-individual"
              className="cta-gold-outline inline-flex items-center justify-center gap-3 px-7 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.14em]"
            >
              Egyéni Forrás-Kód® Kísérés
              <span aria-hidden="true">→</span>
            </Link>
            <Link
              to="/konzultacio#kapcsolat"
              data-testid="fkk-closing-cta-group"
              className="cta-gold-outline inline-flex items-center justify-center gap-3 px-7 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.14em]"
            >
              Csoportos Forrás-Kód® folyamatok
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ===================================================================== */
/* ProcessSteps — „Hogyan zajlik a közös folyamat?" 3 lépcső              */
/* ===================================================================== */
const PROCESS_STEPS = [
  {
    number: "01",
    title: "Feltérképezés",
    text:
      "Közösen feltérképezzük azokat az élethelyzeteket, visszatérő mintázatokat és összefüggéseket, amelyek hatással vannak a jelenlegi működésedre.",
  },
  {
    number: "02",
    title: "Mélyebb érzékelés és felismerések",
    text:
      "A testérzeteken, érzelmeken, belső képeken és érzékelésen keresztül új nézőpontok nyílhatnak meg, és láthatóvá válhatnak az eddig rejtett összefüggések.",
  },
  {
    number: "03",
    title: "Új lehetőségek és tudatos választás",
    text:
      "A felismeréseket új lehetőségekkel és gyakorlati javaslatokkal egészítjük ki, hogy egy tisztább, szabadabb működés épülhessen a helyükre.",
  },
];

function ProcessSteps() {
  return (
    <section
      data-testid="fkk-process-steps"
      className="relative px-5 md:px-10 py-14 md:py-20 bg-tinted border-t border-hairline"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body">
            — A folyamat
          </p>
          <h2 className="font-serif-display text-[30px] sm:text-[36px] md:text-[42px] leading-[1.2] text-graphite max-w-2xl mx-auto">
            Hogyan zajlik a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              közös folyamat
            </em>
            ?
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PROCESS_STEPS.map((step) => (
            <article
              key={step.number}
              data-testid={`fkk-step-${step.number}`}
              className="bg-warm border border-hairline px-7 md:px-9 py-9 md:py-11 flex flex-col h-full"
              style={{ boxShadow: "0 10px 40px -32px rgba(160, 122, 40, 0.28)" }}
            >
              <p className="font-serif-display italic text-gold text-[36px] md:text-[44px] leading-none mb-5">
                {step.number}
              </p>
              <h3 className="font-serif-display text-graphite text-[20px] md:text-[22px] leading-[1.3] mb-5">
                {step.title}
              </h3>
              <p className="font-body font-light text-graphite text-[15px] md:text-[15.5px] leading-[1.8]">
                {step.text}
              </p>
            </article>
          ))}
        </div>

        <p className="mt-12 md:mt-14 font-serif-display italic text-graphite text-center text-[20px] md:text-[24px] leading-[1.5] max-w-2xl mx-auto">
          A választás és a döntés{" "}
          <span className="text-gold not-italic">szabadsága</span> végig nálad marad.
        </p>
      </div>
    </section>
  );
}

/* ===================================================================== */
/* ServicesOverview — 3 kártyás szolgáltatás-áttekintő                    */
/* ===================================================================== */
const SERVICES_OVERVIEW = [
  {
    testId: "fkk-overview-egyeni",
    tag: "Egyéni kísérés",
    description:
      "Személyre szabott, mélyebb önismereti és érzékelési folyamat, amelyben a saját élethelyzeteidre és lehetőségeidre fókuszálunk.",
    cta: "Megismerem az egyéni kísérést",
    href: "#egyeni-kiseres",
    isAnchor: true,
  },
  {
    testId: "fkk-overview-csoportos",
    tag: "Csoportos kísérés",
    description:
      "Közös térben zajló folyamat, ahol az egyéni felismerések mellett a csoportos kapcsolódás is új nézőpontokat nyithat meg.",
    cta: "Megismerem a csoportos kísérést",
    href: "#csoportos-kiseres",
    isAnchor: true,
  },
  {
    testId: "fkk-overview-elmenyprogram",
    tag: "Élményprogramok",
    description:
      "Intenzívebb, élményalapú kapcsolódás, amely teret ad a mélyebb érzékelésnek, felismeréseknek és új lehetőségek megtapasztalásának.",
    cta: "Megismerem az élményprogramokat",
    href: "/elmenyprogram",
    isAnchor: false,
  },
];

function ServicesOverview() {
  return (
    <section
      data-testid="fkk-services-overview"
      className="relative px-5 md:px-10 py-14 md:py-20 border-t border-hairline"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body">
            — Lehetőségek
          </p>
          <h2 className="font-serif-display text-[30px] sm:text-[36px] md:text-[42px] leading-[1.2] text-graphite max-w-2xl mx-auto">
            Hogyan kísérhetlek az{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              utadon
            </em>
            ?
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {SERVICES_OVERVIEW.map((s) => (
            <article
              key={s.testId}
              data-testid={s.testId}
              className="bg-warm border border-hairline hover:border-gold transition-colors duration-500 px-7 md:px-9 py-9 md:py-11 flex flex-col h-full text-center"
              style={{ boxShadow: "0 10px 40px -32px rgba(160, 122, 40, 0.22)" }}
            >
              <p className="font-body text-[10.5px] md:text-[11px] tracking-[0.35em] uppercase text-gold mb-5">
                — {s.tag}
              </p>
              <p className="font-body font-light text-graphite text-[15px] md:text-[16px] leading-[1.85] mb-8 max-w-sm mx-auto">
                {s.description}
              </p>
              <div className="flex-grow min-h-[8px]" aria-hidden="true" />
              {s.isAnchor ? (
                <a
                  href={s.href}
                  data-testid={`${s.testId}-cta`}
                  className="cta-gold-outline inline-flex items-center justify-center gap-3 px-6 py-3.5 text-[11px] md:text-[11.5px] uppercase font-body font-medium tracking-[0.2em]"
                >
                  {s.cta}
                  <span aria-hidden="true">→</span>
                </a>
              ) : (
                <Link
                  to={s.href}
                  data-testid={`${s.testId}-cta`}
                  className="cta-gold-outline inline-flex items-center justify-center gap-3 px-6 py-3.5 text-[11px] md:text-[11.5px] uppercase font-body font-medium tracking-[0.2em]"
                >
                  {s.cta}
                  <span aria-hidden="true">→</span>
                </Link>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/* FinalInvitation — záró jelentkezési szekció két CTA-val                */
/* ===================================================================== */
function FinalInvitation() {
  return (
    <section
      id="jelentkezes"
      data-testid="fkk-final-invitation"
      className="relative px-6 md:px-10 py-14 md:py-20 border-t border-hairline"
    >
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="font-serif-display text-[28px] sm:text-[34px] md:text-[40px] leading-[1.25] text-graphite mb-8 md:mb-10">
          Milyen{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            új lehetőségeket
          </em>
          <br />
          nyithat meg számodra a közös munka?
        </h2>
        <p className="font-body font-light text-graphite text-[16px] md:text-[17px] leading-[1.85] max-w-xl mx-auto mb-10 md:mb-12">
          Ha megszólított valamelyik gondolat, és szeretnéd közelebbről megismerni a
          Forrás-Kód<sup className="text-gold">®</sup> kísérés lehetőségeit, válaszd ki
          a számodra megfelelő kapcsolódási formát.
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 sm:gap-5">
          <a
            href="#szolgaltatasok"
            data-testid="fkk-final-cta-services"
            className="cta-gold-outline inline-flex items-center justify-center gap-3 px-7 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.22em]"
          >
            Kísérési lehetőségek megtekintése
            <span aria-hidden="true">→</span>
          </a>
          <Link
            to="/kapcsolat"
            data-testid="fkk-final-cta-contact"
            className="cta-graphite inline-flex items-center justify-center gap-3 px-7 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.22em]"
          >
            Kapcsolatfelvétel
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
